import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";

export interface DropdownItem {
  label: string;
  icon?: React.ReactNode;
  /** Rendered after the label, e.g. an inline action button */
  suffix?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Highlights the item as the current value */
  selected?: boolean;
  className?: string;
}

export interface DropdownProps {
  trigger: React.ReactNode;
  items: DropdownItem[];
  className?: string;
  align?: "left" | "right";
  width?: "auto" | "sm" | "md" | "lg";
  /** Renders the menu in a portal to avoid clipping by overflow ancestors (recommended in modals). */
  usePortal?: boolean;
  /** Match the trigger width when using portal. */
  matchTriggerWidth?: boolean;
  /** z-index for the menu; keep above your modal (modal overlay is often z-50). */
  zIndex?: number;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  triggerClassName?: string;
  closeOnSelect?: boolean;
  /** Rendered pinned above the items, e.g. a search box */
  header?: React.ReactNode;
}

export const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  className,
  align = "left",
  width = "auto",
  usePortal = true,
  matchTriggerWidth = true,
  zIndex = 200,
  disabled = false,
  label,
  required = false,
  triggerClassName,
  closeOnSelect = true,
  header,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{
    top: number;
    left?: number;
    right?: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const widthClasses = {
    auto: "min-w-[160px]",
    sm: "w-32",
    md: "w-48",
    lg: "w-64",
  };

  const alignClasses = {
    left: "left-0",
    right: "right-0",
  };

  const handleToggle = () => setIsOpen((o) => !o);

  // Close on outside click (works for both in-dom and portal menus)
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Close on Escape
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setIsOpen(false);
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, []);

  // Measure trigger & update fixed position for portal menu
  const updateCoords = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const margin = 8; // space from viewport edges

    const defaultMaxHeight = viewportHeight * 0.4; // 40% of screen
    const availableBelow = viewportHeight - r.bottom - margin;

    // height we can safely use
    const maxHeight = Math.max(
      Math.min(defaultMaxHeight, availableBelow),
      160, // at least some usable height
    );

    // make sure dropdown itself stays fully within viewport
    const top = Math.min(r.bottom + 4, viewportHeight - margin - maxHeight);

    setCoords({
      top,
      left: align === "left" ? r.left : undefined,
      right: align === "right" ? viewportHeight - r.right : undefined,
      width: r.width,
      maxHeight,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen || !usePortal) return;
    updateCoords();
    const onResize = () => updateCoords();
    // capture=true ensures scroll events from scrollable ancestors also trigger
    const onScroll = () => updateCoords();

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [isOpen, usePortal, align]);

  // useLayoutEffect(() => {
  //   if (!isOpen) return;

  //   const handleScroll = () => {
  //     setIsOpen(false); // 👈 Close dropdown when scrolling
  //   };

  //   window.addEventListener("scroll", handleScroll, true);

  //   return () => {
  //     window.removeEventListener("scroll", handleScroll, true);
  //   };
  // }, [isOpen]);

  const menuContent = (
    <div
      ref={menuRef}
      className={cn(
        "rounded-lg border border-gray-200 dark:border-gray-700",
        "bg-white dark:bg-gray-800 shadow-lg py-1",
        "max-h-[40vh] overflow-auto",
        width === "auto" && widthClasses[width],
      )}
      style={
        usePortal
          ? {
            position: "fixed",
            top: coords?.top,
            left: coords?.left,
            right: coords?.right,
            width: matchTriggerWidth ? coords?.width : undefined,
            zIndex,
            maxHeight: coords?.maxHeight,
          }
          : { zIndex }
      }
      role="menu"
    >
      {header && (
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 px-2 pb-1">
          {header}
        </div>
      )}
      {items.length === 0 ? (
        <div className="px-3 py-1 text-center text-sm text-gray-500 dark:text-gray-400">
          No data found
        </div>
      ) : (
        items.map((item, index) => (
          <button
            key={index}
            onClick={() => {
              if (!item.disabled) {
                item.onClick();
                if (closeOnSelect) setIsOpen(false);
              }
            }}
            disabled={item.disabled}
            className={cn(
              "w-full px-3 py-2 text-left text-sm flex items-center gap-2",
              "hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors",
              item.disabled && "opacity-50 cursor-not-allowed",
              item.selected && "bg-primary-100 font-medium text-primary-700 dark:bg-gray-700 dark:text-primary-100",
              item.className,
            )}
            role="menuitem"
          >
            {item.icon && <span className="shrink-0">{item.icon}</span>}
            <span className="truncate flex-1" title={item.label}>{item.label}</span>
            {item.suffix && <span className="shrink-0">{item.suffix}</span>}
          </button>
        ))
      )}
    </div>
  );

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}
      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={() => {
          if (!disabled) handleToggle(); // ✅ prevent toggle when disabled
        }}
        className={cn(
          "cursor-pointer py-2",
          triggerClassName,
          disabled && "opacity-50 cursor-not-allowed", // ✅ add visual indication
        )}
      >
        {trigger}
      </div>

      {/* Menu */}
      {isOpen &&
        (usePortal ? (
          createPortal(menuContent, document.body)
        ) : (
          <div
            className={cn(
              "absolute top-full mt-1",
              "z-[200]", // above modal content, but still under portal menus
              width === "auto" && "w-auto",
              alignClasses[align],
            )}
          >
            {menuContent}
          </div>
        ))}
    </div>
  );
};
